import { useNode, useEditor } from "@craftjs/core";
import {
  faDroplet,
  faEdit,
  faFill,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useMemo, useContext, useEffect } from "react";
import { GrigoraDashboardContext } from "../../dashboard/context";
import {
  addNodeTree,
  handleSaveComponent,
} from "../../editor/component-blocks";
import { GrigoraEditorContext } from "../../editor/context";
import { generate_new_id } from "../../helpers/random";
import { remove_prefix } from "../../helpers";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  ToggleInput,
  BoxShadowInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  TextInput,
  BorderRadiusInput,
  BorderInput,
  GradientInput,
  RadioButtonInput,
  Responsive,
  RangeInput,
  TabsV2,
  RangeNumberInput,
  FilterInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import SelectRadioButtonInput from "../../input/select-radio-buttons";
import {
  AlignCenterIcon,
  AlignContentAroundIcon,
  AlignContentBetweenIcon,
  AlignContentCenterIcon,
  AlignContentEndIcon,
  AlignContentSpaceEvenIcon,
  AlignContentTopIcon,
  AlignEndIcon,
  AlignStartIcon,
  DisplayBlockIcon,
  FlexColumnIcon,
  FlexIcon,
  FlexNoWrapIcon,
  FlexRowIcon,
  FlexWrapIcon,
  JustifyAroundIcon,
  JustifyBetweenIcon,
  JustifyCenterIcon,
  JustifyLeftIcon,
  JustifyRightIcon,
  JustifySpaceEvenIcon,
} from "../../../assets/ui-icons";
import {
  changeTransition,
  isUpdatedFromDefault,
  getDirectoryInfoFromParents,
  getDirectoryFieldOptions,
} from "../../utils";
import { ImagePickerUIKit } from "../../ui-kit";
import { INPUT_LIMITS } from "../../input-limits";

function SourceImage({ projectID, src, srcMode, onClick }) {
  const imgsrc = useMemo(() => {
    if (srcMode === "cdn") {
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${
          src?.projectID ? src.projectID : projectID
        }/${src?.og}`;
      }
      return `https://cdn.grigora.co/projects/${
        src?.projectID ? src.projectID : projectID
      }/${src?.name}.${src?.extension}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
    return undefined;
  }, [srcMode, src]);

  return (
    <div onClick={onClick} className="relative">
      <div className="absolute flex top-0 bottom-0 left-0 right-0 justify-center items-center duration-300 hover:bg-black">
        <FontAwesomeIcon icon={faEdit} />
      </div>
      <img src={imgsrc} />
    </div>
  );
}

export const ContainerSettings = () => {
  const { addComponent, projectComponents, pagesData, currentPage } =
    useContext(GrigoraEditorContext) ?? {};
  const { projectID, directoryTypes } =
    useContext(GrigoraDashboardContext) ?? {};

  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
    query,
    actions,
  } = useEditor();

  const parents = node(id).ancestors(true);

  const [bgImagePickerModal, setbgImagePickerModal] = useState(false);
  const [hoverBgImagePickerModal, setHoverbgImagePickerModal] = useState(false);

  const { displaySize } = useContext(GrigoraEditorContext);

  const {
    Tag = "div",
    display,
    conditional,
    width = "100%",
    widthTablet = "100%",
    widthMobile = "100%",
    height,
    heightTablet,
    heightMobile,
    maxWidth,
    maxWidthTablet,
    maxWidthMobile,
    maxHeight,
    maxHeightTablet,
    maxHeightMobile,
    blockGap = "1rem",
    blockGapTablet,
    blockGapMobile,
    padding = {
      top: "20px",
      bottom: "20px",
      left: "20px",
      right: "20px",
    },
    paddingTablet,
    paddingMobile,
    margin,
    marginTablet,
    marginMobile,
    hoverPadding,
    hoverPaddingTablet,
    hoverPaddingMobile,
    hoverMargin,
    hoverMarginTablet,
    hoverMarginMobile,
    hoverTextColor,
    hoverLinkColor,
    hoverBorder,
    hoverBorderTablet,
    hoverBorderMobile,
    hoverBoxShadow,
    overflow,
    textColor,
    linkColor,
    border,
    borderTablet,
    borderMobile,
    borderRadius,
    hoverBorderRadius,
    boxShadow,
    alignItems,
    alignItemsTablet,
    alignItemsMobile,
    url,
    isUrlDynamic,
    dynamicUrlSrc,
    dynamicUrlSrcData,
    justifyContent,
    justifyContentTablet,
    justifyContentMobile,
    openInNewTab,
    minWidth,
    minWidthTablet,
    minWidthMobile,
    minHeight,
    minHeightTablet,
    minHeightMobile,
    direction = "column",
    directionTablet,
    directionMobile,
    wrap,
    wrapTablet,
    wrapMobile,
    isBackgroundLayerDynamic,
    hoverIsBackgroundLayerDynamic,
    dynamicBackgroundLayerSrc,
    hoverDynamicBackgroundLayerSrc,
    backgroundLayer,
    backgroundLayerTablet,
    backgroundLayerMobile,
    hoverBackgroundLayer,
    hoverBackgroundLayerTablet,
    hoverBackgroundLayerMobile,
    backgroundOverlayLayer,
    backgroundOverlayLayerTablet = 0.7,
    backgroundOverlayLayerMobile,
    hoverBackgroundOverlayLayer,
    hoverBackgroundOverlayLayerTablet = 0.7,
    hoverBackgroundOverlayLayerMobile,
    backgroundOverlayLayerOpacity,
    backgroundOverlayLayerOpacityTablet,
    backgroundOverlayLayerOpacityMobile,
    hoverBackgroundOverlayLayerOpacity = 0.7,
    hoverBackgroundOverlayLayerOpacityTablet,
    hoverBackgroundOverlayLayerOpacityMobile,
    isComponent,
    componentID,
    linkDecoration,
    hideDesktop,
    hideTablet,
    hideMobile,
    transition,
  } = props;

  const [activeDevice, setActiveDevice] = useState("desktop");

  const [isCreatingComponent, setIsCreatingComponent] = useState(false);
  const [newComponentName, setNewComponentName] = useState("");

  useEffect(() => {
    if (displaySize) {
      setActiveDevice(displaySize);
    }
  }, [displaySize]);

  // Effect to auto-select directory source when appropriate
  useEffect(() => {
    if (props.isUrlDynamic && !props.dynamicUrlSrc) {
      const directoryInfo = getDirectoryInfoFromParents(parents, node);
      if (directoryInfo) {
        setProp((props) => {
          props.dynamicUrlSrc = "directory-listing";
          props.dynamicUrlSrcData = {
            directory_type_id: directoryInfo.directoryTypeId,
            directory_field_id: "current-listing",
          };
        });
      } else {
        // Check if current page is a directory listing page
        var currentPageData = pagesData?.[currentPage] ?? {};

        if (
          currentPageData?.type === "template" &&
          currentPageData?.template === "directory-template" &&
          currentPageData?.directory_id
        ) {
          var pageDirectoryId = currentPageData?.directory_id;

          var directoryType = directoryTypes?.find(
            (type) =>
              remove_prefix(type.sk, "directory-type-") === pageDirectoryId,
          );
          if (directoryType) {
            setProp((props) => {
              props.dynamicUrlSrc = "directory-listing";
              props.dynamicUrlSrcData = {
                directory_type_id: pageDirectoryId,
                directory_field_id: "current-listing",
              };
            });
          }
        }
      }
    }
  }, [props.isUrlDynamic]);

  function findImageField(directoryTypeId) {
    var allFields = directoryTypes?.find(
      (type) => remove_prefix(type.sk, "directory-type-") === directoryTypeId,
    )?.fields;
    var imageFields = allFields?.filter((field) => field.type === "image");
    if (imageFields?.length > 0) {
      return imageFields[0].id;
    }
  }

  // Effect to auto-select directory source when appropriate
  useEffect(() => {
    console.log(props.isBackgroundLayerDynamic);
    if (props.isBackgroundLayerDynamic && !props.dynamicBackgroundLayerSrc) {
      const directoryInfo = getDirectoryInfoFromParents(parents, node);
      if (directoryInfo) {
        setProp((props) => {
          props.dynamicBackgroundLayerSrc = "directory-listing";
          props.backgroundLayer = {
            image: {
              ...props.backgroundLayer?.image,
              directoryTypeId: directoryInfo.directoryTypeId,
              directoryFieldId: findImageField(directoryInfo.directoryTypeId),
            },
          };
        });
      } else {
        // Check if current page is a directory listing page
        var currentPageData = pagesData?.[currentPage] ?? {};

        if (
          currentPageData?.type === "template" &&
          currentPageData?.template === "directory-template" &&
          currentPageData?.directory_id
        ) {
          var pageDirectoryId = currentPageData?.directory_id;

          var directoryType = directoryTypes?.find(
            (type) =>
              remove_prefix(type.sk, "directory-type-") === pageDirectoryId,
          );
          if (directoryType) {
            setProp((props) => {
              props.dynamicBackgroundLayerSrc = "directory-listing";
              props.backgroundLayer = {
                image: {
                  ...props.backgroundLayer?.image,
                  directoryTypeId: pageDirectoryId,
                  directoryFieldId: findImageField(pageDirectoryId),
                },
              };
            });
          }
        }
      }
    }
  }, [props.isBackgroundLayerDynamic]);

  useEffect(() => {
    if (
      props.hoverIsBackgroundLayerDynamic &&
      !props.hoverDynamicBackgroundLayerSrc
    ) {
      const directoryInfo = getDirectoryInfoFromParents(parents, node);
      if (directoryInfo) {
        setProp((props) => {
          props.hoverDynamicBackgroundLayerSrc = "directory-listing";
          props.hoverBackgroundLayer = {
            image: {
              ...props.hoverBackgroundLayer?.image,
              directoryTypeId: directoryInfo.directoryTypeId,
              directoryFieldId: findImageField(directoryInfo.directoryTypeId),
            },
          };
        });
      } else {
        // Check if current page is a directory listing page
        var currentPageData = pagesData?.[currentPage] ?? {};

        if (
          currentPageData?.type === "template" &&
          currentPageData?.template === "directory-template" &&
          currentPageData?.directory_id
        ) {
          var pageDirectoryId = currentPageData?.directory_id;

          var directoryType = directoryTypes?.find(
            (type) =>
              remove_prefix(type.sk, "directory-type-") === pageDirectoryId,
          );

          if (directoryType) {
            setProp((props) => {
              props.hoverDynamicBackgroundLayerSrc = "directory-listing";
              props.hoverBackgroundLayer = {
                image: {
                  ...props.hoverBackgroundLayer?.image,
                  directoryTypeId: pageDirectoryId,
                  directoryFieldId: findImageField(pageDirectoryId),
                },
              };
            });
          }
        }
      }
    }
  }, [props.hoverIsBackgroundLayerDynamic]);

  var hoverBackground;
  if (activeDevice === "desktop") {
    hoverBackground = hoverBackgroundLayer;
  } else if (activeDevice === "tablet") {
    hoverBackground = hoverBackgroundLayerTablet;
  } else {
    hoverBackground = hoverBackgroundLayerMobile;
  }

  var background;
  if (activeDevice === "desktop") {
    background = backgroundLayer;
  } else if (activeDevice === "tablet") {
    background = backgroundLayerTablet;
  } else {
    background = backgroundLayerMobile;
  }

  var backgroundOverlay;
  if (activeDevice === "desktop") {
    backgroundOverlay = backgroundOverlayLayer;
  } else if (activeDevice === "tablet") {
    backgroundOverlay = backgroundOverlayLayerTablet;
  } else {
    backgroundOverlay = backgroundOverlayLayerMobile;
  }

  var hoverBackgroundOverlay;
  if (activeDevice === "desktop") {
    hoverBackgroundOverlay = hoverBackgroundOverlayLayer;
  } else if (activeDevice === "tablet") {
    hoverBackgroundOverlay = hoverBackgroundOverlayLayerTablet;
  } else {
    hoverBackgroundOverlay = hoverBackgroundOverlayLayerMobile;
  }

  const hasParentAnchorFn = (parents) => {
    for (let i = 0; i < parents.length; i++) {
      if (node(parents[i]).get().data.custom.slug === "grigora/container") {
        if (!!node(parents[i]).get().data.props.url) return true;
      }
    }
    return false;
  };

  var hasParentAnchor = useMemo(() => hasParentAnchorFn(parents), [parents]);

  const DISPLAY = [
    {
      label: "Block",
      value: "block",
      icon: <DisplayBlockIcon />,
      tooltip: "Block",
    },
    {
      label: "Flex",
      value: "flex",
      icon: <FlexIcon />,
      tooltip: "Flex",
    },
  ];

  const DIRECTION = [
    {
      label: "Column",
      value: "column",
      tooltip: "Column",
      icon: <FlexColumnIcon />,
    },
    {
      label: "Row",
      value: "row",
      tooltip: "Row",
      icon: <FlexRowIcon />,
    },
  ];

  const WRAP = [
    {
      label: "Wrap",
      value: "wrap",
      tooltip: "Wrap",
      icon: <FlexWrapIcon />,
    },
    {
      label: "Nowrap",
      value: "nowrap",
      tooltip: "Nowrap",
      icon: <FlexNoWrapIcon />,
    },
  ];

  const JUSTIFYCONTENT = [
    {
      label: "Start",
      value: "start",
      tooltip: "Start",
      icon: <AlignContentTopIcon />,
    },
    {
      label: "Center",
      value: "center",
      tooltip: "Center",
      icon: <AlignContentCenterIcon />,
    },
    {
      label: "End",
      value: "end",
      tooltip: "End",
      icon: <AlignContentEndIcon />,
    },
    {
      label: "Space Between",
      value: "space-between",
      tooltip: "Space-between",
      icon: <AlignContentBetweenIcon />,
    },
    {
      label: "Space Around",
      value: "space-around",
      tooltip: "Space-around",
      icon: <AlignContentAroundIcon />,
    },
    {
      label: "Space Evenly",
      value: "space-evenly",
      tooltip: "Space Evenly",
      icon: <AlignContentSpaceEvenIcon />,
    },
  ];

  const ALIGNITEMS = [
    {
      label: "Start",
      value: "start",
      tooltip: "Start",
      icon: <AlignStartIcon />,
    },
    {
      label: "Center",
      value: "center",
      tooltip: "Center",
      icon: <AlignCenterIcon />,
    },
    {
      label: "End",
      value: "end",
      tooltip: "End",
      icon: <AlignEndIcon />,
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: display, default: "block" },
            { key: direction, default: "column" },
            { key: directionTablet },
            { key: directionMobile },
            { key: wrap, default: "wrap" },
            { key: wrapTablet },
            { key: wrapMobile },
            { key: alignItemsTablet },
            { key: alignItemsMobile },
            { key: alignItems, default: "start" },
            { key: justifyContentTablet },
            { key: justifyContentMobile },
            { key: justifyContent, default: "start" },
            { key: blockGap, default: "1rem" },
            { key: blockGapTablet },
            { key: blockGapMobile },
          ])}
          id={`block-setting-${id}-display-properties`}
          title={"Display Properties"}
          initialOpen={true}
        >
          <SelectRadioButtonInput
            isResponsive={false}
            resetValue={undefined}
            value={display}
            onChange={(val) => {
              setProp((props) => {
                props.display = val;
              });
            }}
            options={DISPLAY}
            label="Display"
          />
          {display === "flex" && (
            <>
              <SelectRadioButtonInput
                resetValue={"column"}
                isResponsive={true}
                value={direction}
                valueTablet={directionTablet}
                valueMobile={directionMobile}
                onChange={(val) => {
                  setProp((props) => {
                    props.direction = val;
                  });
                }}
                setValueTablet={(val) => {
                  setProp((props) => {
                    props.directionTablet = val;
                  });
                }}
                setValueMobile={(val) => {
                  setProp((props) => {
                    props.directionMobile = val;
                  });
                }}
                options={DIRECTION}
                label="Direction"
              />
              <SelectRadioButtonInput
                resetValue={undefined}
                isResponsive={true}
                value={wrap}
                valueTablet={wrapTablet}
                valueMobile={wrapMobile}
                onChange={(val) => {
                  setProp((props) => {
                    props.wrap = val;
                  });
                }}
                setValueTablet={(val) => {
                  setProp((props) => {
                    props.wrapTablet = val;
                  });
                }}
                setValueMobile={(val) => {
                  setProp((props) => {
                    props.wrapMobile = val;
                  });
                }}
                options={WRAP}
                label="Wrap"
              />
              {(displaySize === "desktop" && direction === "column") ||
              (displaySize === "tablet" &&
                (direction === "column" || directionTablet === "column")) ||
              (displaySize === "mobile" &&
                (direction === "column" || directionMobile === "column")) ? (
                <>
                  <SelectRadioButtonInput
                    isResponsive={true}
                    valueTablet={alignItemsTablet}
                    valueMobile={alignItemsMobile}
                    resetValue={undefined}
                    value={alignItems}
                    onChange={(val) => {
                      setProp((props) => {
                        props.alignItems = val;
                      });
                    }}
                    setValueTablet={(val) => {
                      setProp((props) => {
                        props.alignItemsTablet = val;
                      });
                    }}
                    setValueMobile={(val) => {
                      setProp((props) => {
                        props.alignItemsMobile = val;
                      });
                    }}
                    options={ALIGNITEMS}
                    label="Align"
                  />
                  <SelectRadioButtonInput
                    layout="newline"
                    isResponsive={true}
                    valueTablet={justifyContentTablet}
                    valueMobile={justifyContentMobile}
                    resetValue={undefined}
                    value={justifyContent}
                    onChange={(val) => {
                      setProp((props) => {
                        props.justifyContent = val;
                      });
                    }}
                    setValueTablet={(val) => {
                      setProp((props) => {
                        props.justifyContentTablet = val;
                      });
                    }}
                    setValueMobile={(val) => {
                      setProp((props) => {
                        props.justifyContentMobile = val;
                      });
                    }}
                    options={JUSTIFYCONTENT}
                    label="Vertical Align"
                  />
                </>
              ) : (
                <>
                  <SelectRadioButtonInput
                    rotateIcon={-90}
                    layout="newline"
                    resetValue={undefined}
                    isResponsive={true}
                    value={justifyContent}
                    valueTablet={justifyContentTablet}
                    valueMobile={justifyContentMobile}
                    onChange={(val) => {
                      setProp((props) => {
                        props.justifyContent = val;
                      });
                    }}
                    setValueTablet={(val) => {
                      setProp((props) => {
                        props.justifyContentTablet = val;
                      });
                    }}
                    setValueMobile={(val) => {
                      setProp((props) => {
                        props.justifyContentMobile = val;
                      });
                    }}
                    options={JUSTIFYCONTENT}
                    label="Align"
                  />
                  <SelectRadioButtonInput
                    rotateIcon={90}
                    resetValue={undefined}
                    isResponsive={true}
                    value={alignItems}
                    valueTablet={alignItemsTablet}
                    valueMobile={alignItemsMobile}
                    onChange={(val) => {
                      setProp((props) => {
                        props.alignItems = val;
                      });
                    }}
                    setValueTablet={(val) => {
                      setProp((props) => {
                        props.alignItemsTablet = val;
                      });
                    }}
                    setValueMobile={(val) => {
                      setProp((props) => {
                        props.alignItemsMobile = val;
                      });
                    }}
                    options={ALIGNITEMS}
                    label="Vertical Align"
                  />
                </>
              )}
            </>
          )}
          <UnitInput
            isResponsive={true}
            resetValue={"1rem"}
            value={blockGap}
            valueTablet={blockGapTablet}
            valueMobile={blockGapMobile}
            onChange={(val) => {
              setProp((props) => {
                props.blockGap = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.blockGapTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.blockGapMobile = val;
              });
            }}
            label={"Gap"}
            unit={{ label: "rem", value: "rem" }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: width, default: "100%" },
            { key: widthTablet, default: "100%" },
            { key: widthMobile, default: "100%" },
            { key: height },
            { key: heightTablet },
            { key: heightMobile },
            { key: maxHeight },
            { key: maxHeightTablet },
            { key: maxHeightMobile },
            { key: maxWidthTablet },
            { key: maxWidth },
            { key: maxWidthMobile },
            { key: minHeightMobile },
            { key: minHeightTablet },
            { key: minHeight },
            { key: minWidth },
            { key: minWidthMobile },
            { key: minWidthTablet },
            { key: Tag, default: "div" },
            { key: overflow },
            { key: marginTablet },
            { key: marginMobile },
            { key: margin },
            { key: paddingTablet },
            { key: paddingMobile },
            {
              key: padding,
              default: {
                top: "20px",
                bottom: "20px",
                left: "20px",
                right: "20px",
              },
            },
            { key: hoverMarginTablet },
            { key: hoverMarginMobile },
            { key: hoverMargin },
            { key: hoverPaddingTablet },
            { key: hoverPaddingMobile },
            { key: hoverPadding },
          ])}
          id={`block-setting-${id}-dimensions`}
          title={"Dimensions"}
          initialOpen={true}
        >
          <UnitInput
            auto
            value={width}
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
            resetValue={"100%"}
            resetValueTablet={"100%"}
            resetValueMobile={"100%"}
          />
          <UnitInput
            resetValue={undefined}
            auto
            value={height}
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={maxHeight}
            isResponsive={true}
            valueTablet={maxHeightTablet}
            valueMobile={maxHeightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.maxHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.maxHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.maxHeightMobile = val;
              });
            }}
            label={"Max Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={maxWidth}
            isResponsive={true}
            valueTablet={maxWidthTablet}
            valueMobile={maxWidthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.maxWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.maxWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.maxWidthMobile = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            valueMobile={minHeightMobile}
            valueTablet={minHeightTablet}
            value={minHeight}
            onChange={(val) => {
              setProp((props) => {
                props.minHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.minHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.minHeightMobile = val;
              });
            }}
            label={"Min Height"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            valueMobile={minWidthMobile}
            valueTablet={minWidthTablet}
            value={minWidth}
            onChange={(val) => {
              setProp((props) => {
                props.minWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.minWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.minWidthMobile = val;
              });
            }}
            label={"Min Width"}
          />
          <SelectInput
            resetValue={"div"}
            value={Tag}
            onChange={(val) => {
              setProp((props) => {
                props.Tag = val;
              });
            }}
            options={[
              { label: "Div", value: "div" },
              { label: "Main", value: "main" },
              { label: "Aside", value: "aside" },
              { label: "Section", value: "section" },
              { label: "Header", value: "header" },
              { label: "Footer", value: "footer" },
            ]}
            label="Container Tag"
          />
          <SelectInput
            resetValue={undefined}
            value={overflow}
            onChange={(val) => {
              setProp((props) => {
                props.overflow = val;
              });
            }}
            options={[
              { label: "Visible", value: "visible" },
              { label: "Hidden", value: "hidden" },
              { label: "Clip", value: "clip" },
              { label: "Scroll", value: "scroll" },
              { label: "Auto", value: "auto" },
              { label: "Inherit", value: "inherit" },
              { label: "Initial", value: "initial" },
              { label: "Revert", value: "revert" },
              { label: "Revert-layer", value: "revert-layer" },
              { label: "Unset", value: "unset" },
            ]}
            label="Overflow"
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: url },
            { key: openInNewTab },
            { key: linkDecoration },
            { key: isUrlDynamic },
            { key: dynamicUrlSrc },
          ])}
          id={`block-setting-${id}-link`}
          title={"Link"}
          initialOpen={false}
        >
          {hasParentAnchor ? (
            <>Parent has anchor</>
          ) : (
            <>
              {isUrlDynamic ? (
                <></>
              ) : (
                <>
                  <TextInput
                    minLength={INPUT_LIMITS["outbound-link"].minlength}
                    maxLength={INPUT_LIMITS["outbound-link"].maxlength}
                    resetValue={undefined}
                    label={"URL"}
                    value={url}
                    onChange={(val) => {
                      setProp((props) => {
                        props.url = val;
                      });
                    }}
                  />
                </>
              )}
            </>
          )}
          <ToggleInput
            resetValue={undefined}
            label="Open in new tab"
            value={openInNewTab}
            onChange={(val) => {
              setProp((props) => {
                props.openInNewTab = val;
              });
            }}
          />
          <ToggleInput
            resetValue={undefined}
            label="Remove Link Decoration"
            value={linkDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.linkDecoration = val;
              });
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="Dynamic Source"
            value={isUrlDynamic}
            onChange={(val) => {
              setProp((props) => {
                props.isUrlDynamic = val;
              });
            }}
          />
          {isUrlDynamic ? (
            <>
              <SelectInput
                isResetVisible={false}
                label="Src"
                value={dynamicUrlSrc}
                onChange={(val) => {
                  setProp((props) => {
                    props.dynamicUrlSrc = val;
                  });
                }}
                options={[
                  {
                    label: "-",
                    value: "",
                  },
                  {
                    label: "Website's Home Page",
                    value: "home-page",
                  },
                  {
                    label: "Current CMS Post",
                    value: "current-cms-post",
                  },
                  {
                    label: "Current Category Page",
                    value: "current-cms-category",
                  },
                  {
                    label: "Directory Listing",
                    value: "directory-listing",
                  },
                ]}
              />
              {dynamicUrlSrc === "directory-listing" &&
                (directoryTypes?.length > 0 ? (
                  <>
                    <SelectInput
                      isResetVisible={false}
                      label="Directory"
                      value={dynamicUrlSrcData?.directory_type_id}
                      onChange={(val) => {
                        setProp((props) => {
                          props.dynamicUrlSrcData = {
                            ...(props.dynamicUrlSrcData ?? {}),
                            directory_type_id: val,
                          };
                        });
                      }}
                      options={[
                        {
                          label: "Select Directory",
                          value: "",
                        },
                        ...directoryTypes?.map((type) => ({
                          label: type.name,
                          value: remove_prefix(type.sk, "directory-type-"),
                        })),
                      ]}
                    />
                    {dynamicUrlSrcData?.directory_type_id && (
                      <SelectInput
                        isResetVisible={false}
                        label="Link Field"
                        value={dynamicUrlSrcData?.directory_field_id}
                        onChange={(val) => {
                          setProp((props) => {
                            props.dynamicUrlSrcData = {
                              ...(props.dynamicUrlSrcData ?? {}),
                              directory_field_id: val,
                            };
                          });
                        }}
                        options={getDirectoryFieldOptions(
                          directoryTypes,
                          dynamicUrlSrcData?.directory_type_id,
                          ["link"],
                          true,
                        )}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-500">
                      No directory types found
                    </span>
                  </>
                ))}
            </>
          ) : (
            <></>
          )}
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverSettings(),
      },
    ];
    function normalSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            value={textColor}
            onChange={(val) => {
              setProp((props) => {
                props.textColor = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={linkColor}
            onChange={(val) => {
              setProp((props) => {
                props.linkColor = val;
              });
            }}
            label={"Link"}
          />
        </>
      );
    }
    function hoverSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            value={hoverTextColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverTextColor = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={hoverLinkColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverLinkColor = val;
              });
            }}
            label={"Link"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("color", "background-color")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["color", "background-color"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    const backgroundTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBackgroundSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBackgroundSettings(),
      },
    ];
    function normalBackgroundSettings() {
      return (
        <>
          <ToggleInput
            isResetVisible={false}
            label="Dynamic Source"
            value={isBackgroundLayerDynamic}
            onChange={(val) => {
              setProp((props) => {
                props.isBackgroundLayerDynamic = val;
              });
            }}
          />
          {isBackgroundLayerDynamic ? (
            <>
              <SelectInput
                isResetVisible={false}
                label="Src"
                value={dynamicBackgroundLayerSrc}
                onChange={(val) => {
                  setProp((props) => {
                    props.dynamicBackgroundLayerSrc = val;
                  });
                }}
                options={[
                  {
                    label: "-",
                    value: "",
                  },
                  {
                    label: "Post Featured Image",
                    value: "post-featured-image",
                  },
                  {
                    label: "Category Featured Image",
                    value: "category-featured-image",
                  },
                  {
                    label: "Directory Listing",
                    value: "directory-listing",
                  },
                ]}
              />
              {dynamicBackgroundLayerSrc === "directory-listing" &&
                (directoryTypes?.length > 0 ? (
                  <>
                    <SelectInput
                      isResetVisible={false}
                      label="Directory"
                      value={background?.image?.directoryTypeId}
                      onChange={(val) => {
                        setProp((props) => {
                          var propName;
                          if (activeDevice === "desktop") {
                            propName = "backgroundLayer";
                          } else if (activeDevice === "tablet") {
                            propName = "backgroundLayerTablet";
                          } else {
                            propName = "backgroundLayerMobile";
                          }
                          props[propName] = {
                            ...(props[propName] ? props[propName] : {}),
                            image: {
                              ...(props[propName]?.image
                                ? props[propName].image
                                : {}),
                              directoryTypeId: val,
                            },
                          };
                        });
                      }}
                      options={[
                        {
                          label: "Select Directory",
                          value: "",
                        },
                        ...directoryTypes?.map((type) => ({
                          label: type.name,
                          value: remove_prefix(type.sk, "directory-type-"),
                        })),
                      ]}
                    />
                    {background?.image?.directoryTypeId && (
                      <SelectInput
                        isResetVisible={false}
                        label="Field"
                        value={background?.image?.directoryFieldId}
                        onChange={(val) => {
                          setProp((props) => {
                            var propName;
                            if (activeDevice === "desktop") {
                              propName = "backgroundLayer";
                            } else if (activeDevice === "tablet") {
                              propName = "backgroundLayerTablet";
                            } else {
                              propName = "backgroundLayerMobile";
                            }
                            props[propName] = {
                              ...(props[propName] ? props[propName] : {}),
                              image: {
                                ...(props[propName]?.image
                                  ? props[propName].image
                                  : {}),
                                directoryFieldId: val,
                              },
                            };
                          });
                        }}
                        options={getDirectoryFieldOptions(
                          directoryTypes,
                          background?.image?.directoryTypeId,
                          ["image"],
                        )}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-500">
                      No directory types found
                    </span>
                  </>
                ))}
            </>
          ) : (
            <>
              <div className="flex">
                <div className="flex flex-1">
                  <RadioButtonInput
                    className={"w-full"}
                    value={background?.mode}
                    onChange={(e) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        if (props[propName]?.mode === e) {
                          props[propName] = undefined;
                        } else {
                          props[propName] = {
                            ...(props[propName] ? props[propName] : {}),
                            mode: e,
                            value:
                              e === "color"
                                ? "#ffffff"
                                : e === "gradient"
                                ? "linear-gradient(135deg, rgb(23,144,214) 0%, rgb(155,81,224) 100%)"
                                : undefined,
                          };
                        }
                      });
                    }}
                    options={[
                      {
                        value: "color",
                        label: "Color",
                        icon: <FontAwesomeIcon icon={faFill} />,
                      },
                      {
                        value: "gradient",
                        label: "Gradient",
                        icon: <FontAwesomeIcon icon={faDroplet} />,
                      },
                      {
                        value: "image",
                        label: "Image",
                        icon: <FontAwesomeIcon icon={faImage} />,
                      },
                    ]}
                  />
                </div>
                <div className="flex relative  text-xs mb-2 items-center">
                  <Responsive
                    activeMode={activeDevice}
                    onChangeActiveMode={(e) => {
                      setActiveDevice(e);
                    }}
                  />
                </div>
              </div>
              {background?.mode === "color" && (
                <ColorInput
                  value={background?.value}
                  onChange={(val) => {
                    setProp((props) => {
                      var propName;
                      if (activeDevice === "desktop") {
                        propName = "backgroundLayer";
                      } else if (activeDevice === "tablet") {
                        propName = "backgroundLayerTablet";
                      } else {
                        propName = "backgroundLayerMobile";
                      }
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        value: val,
                      };
                    });
                  }}
                  label={"Color"}
                />
              )}
              {background?.mode === "gradient" && (
                <>
                  <GradientInput
                    value={background?.value}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          value: val,
                        };
                      });
                    }}
                    label={"gradient"}
                    colorPicker="sketch"
                  />
                  <ToggleInput
                    value={background?.fixed}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          fixed: val,
                        };
                      });
                    }}
                    label={"Fixed"}
                  />
                </>
              )}
              {background?.mode === "image" && (
                <>
                  <div className="flex items-center justify-center  cursor-pointer text-sm bg-editor-bg-main-hover duration-300 hover:bg-editor-bg-dark mb-4">
                    <SourceImage
                      projectID={projectID}
                      src={background?.image?.src}
                      srcMode={background?.image?.srcMode}
                      onClick={() => {
                        setbgImagePickerModal(true);
                      }}
                    />
                  </div>
                  <ImagePickerUIKit
                    src={background?.image?.src}
                    srcMode={background?.image?.srcMode}
                    onChange={(src, srcMode) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }

                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          image: {
                            ...(props[propName].image
                              ? props[propName].image
                              : {}),
                            src: src,
                            srcMode: srcMode,
                          },
                        };
                      });
                    }}
                    opened={bgImagePickerModal}
                    setOpened={setbgImagePickerModal}
                  />
                  <SelectInput
                    value={background?.size}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          size: val,
                          sizeCustomWidth: "auto",
                          sizeCustomHeight: "auto",
                        };
                      });
                    }}
                    options={[
                      {
                        label: "Cover",
                        value: "cover",
                      },
                      {
                        label: "Contain",
                        value: "contain",
                      },
                      {
                        label: "Custom",
                        value: "custom",
                      },
                    ]}
                    isResetVisible={false}
                    label={"Size"}
                  />
                  {background?.size === "custom" && (
                    <>
                      <UnitInput
                        isResetVisible={false}
                        auto
                        value={background?.sizeCustomWidth}
                        onChange={(val) => {
                          setProp((props) => {
                            var propName;
                            if (activeDevice === "desktop") {
                              propName = "backgroundLayer";
                            } else if (activeDevice === "tablet") {
                              propName = "backgroundLayerTablet";
                            } else {
                              propName = "backgroundLayerMobile";
                            }
                            props[propName] = {
                              ...(props[propName] ? props[propName] : {}),
                              sizeCustomWidth: val,
                            };
                          });
                        }}
                        label={"Width"}
                      />
                      <UnitInput
                        isResetVisible={false}
                        auto
                        value={background?.sizeCustomHeight}
                        onChange={(val) => {
                          setProp((props) => {
                            var propName;
                            if (activeDevice === "desktop") {
                              propName = "backgroundLayer";
                            } else if (activeDevice === "tablet") {
                              propName = "backgroundLayerTablet";
                            } else {
                              propName = "backgroundLayerMobile";
                            }
                            props[propName] = {
                              ...(props[propName] ? props[propName] : {}),
                              sizeCustomHeight: val,
                            };
                          });
                        }}
                        label={"Height"}
                      />
                    </>
                  )}
                  <div className="text-xs  mb-1">Position</div>
                  <UnitInput
                    isResetVisible={false}
                    value={background?.positionX}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          positionX: val,
                        };
                      });
                    }}
                    label={"Left"}
                  />
                  <UnitInput
                    isResetVisible={false}
                    value={background?.positionY}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          positionY: val,
                        };
                      });
                    }}
                    label={"Top"}
                  />
                  <SelectInput
                    value={background?.repeat}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          repeat: val,
                        };
                      });
                    }}
                    options={[
                      {
                        label: "Both",
                        value: "repeat",
                      },
                      {
                        label: "X",
                        value: "repeat-x",
                      },
                      {
                        label: "Y",
                        value: "repeat-y",
                      },
                      {
                        label: "No Repeat",
                        value: "no-repeat",
                      },
                    ]}
                    isResetVisible={false}
                    label={"Repeat"}
                  />
                  <ToggleInput
                    value={background?.fixed}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundLayerTablet";
                        } else {
                          propName = "backgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          fixed: val,
                        };
                      });
                    }}
                    label={"Fixed"}
                  />
                </>
              )}
            </>
          )}
        </>
      );
    }
    function hoverBackgroundSettings() {
      return (
        <>
          <ToggleInput
            isResetVisible={false}
            label="Dynamic Source"
            value={hoverIsBackgroundLayerDynamic}
            onChange={(val) => {
              setProp((props) => {
                props.hoverIsBackgroundLayerDynamic = val;
              });
            }}
          />
          {hoverIsBackgroundLayerDynamic ? (
            <>
              <SelectInput
                isResetVisible={false}
                label="Src"
                value={hoverDynamicBackgroundLayerSrc}
                onChange={(val) => {
                  setProp((props) => {
                    props.hoverDynamicBackgroundLayerSrc = val;
                  });
                }}
                options={[
                  {
                    label: "-",
                    value: "",
                  },
                  {
                    label: "Post Featured Image",
                    value: "post-featured-image",
                  },
                  {
                    label: "Category Featured Image",
                    value: "category-featured-image",
                  },
                  {
                    label: "Directory Listing",
                    value: "directory-listing",
                  },
                ]}
              />
              {hoverDynamicBackgroundLayerSrc === "directory-listing" &&
                (directoryTypes?.length > 0 ? (
                  <>
                    <SelectInput
                      isResetVisible={false}
                      label="Directory"
                      value={hoverBackground?.image?.directoryTypeId}
                      onChange={(val) => {
                        setProp((props) => {
                          var propName;
                          if (activeDevice === "desktop") {
                            propName = "hoverBackgroundLayer";
                          } else if (activeDevice === "tablet") {
                            propName = "hoverBackgroundLayerTablet";
                          } else {
                            propName = "hoverBackgroundLayerMobile";
                          }
                          props[propName] = {
                            ...(props[propName] ? props[propName] : {}),
                            image: {
                              ...(props[propName]?.image
                                ? props[propName].image
                                : {}),
                              directoryTypeId: val,
                            },
                          };
                        });
                      }}
                      options={[
                        {
                          label: "Select Directory",
                          value: "",
                        },
                        ...directoryTypes?.map((type) => ({
                          label: type.name,
                          value: remove_prefix(type.sk, "directory-type-"),
                        })),
                      ]}
                    />
                    {hoverBackground?.image?.directoryTypeId && (
                      <SelectInput
                        isResetVisible={false}
                        label="Field"
                        value={hoverBackground?.image?.directoryFieldId}
                        onChange={(val) => {
                          setProp((props) => {
                            var propName;
                            if (activeDevice === "desktop") {
                              propName = "hoverBackgroundLayer";
                            } else if (activeDevice === "tablet") {
                              propName = "hoverBackgroundLayerTablet";
                            } else {
                              propName = "hoverBackgroundLayerMobile";
                            }
                            props[propName] = {
                              ...(props[propName] ? props[propName] : {}),
                              image: {
                                ...(props[propName]?.image
                                  ? props[propName].image
                                  : {}),
                                directoryFieldId: val,
                              },
                            };
                          });
                        }}
                        options={[
                          {
                            label: "Select Field",
                            value: "",
                          },
                          ...(directoryTypes
                            ?.find(
                              (type) =>
                                remove_prefix(type.sk, "directory-type-") ===
                                hoverBackground?.image?.directoryTypeId,
                            )
                            ?.fields?.filter((field) => field.type === "image")
                            ?.map((field) => ({
                              label: field.label,
                              value: field.id,
                            })) || []),
                        ]}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-500">
                      No directory types found
                    </span>
                  </>
                ))}
            </>
          ) : (
            <>
              <div className="flex">
                <div className="flex flex-1">
                  <RadioButtonInput
                    className={"w-full"}
                    value={hoverBackground?.mode}
                    onChange={(e) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundLayerTablet";
                        } else {
                          propName = "hoverBackgroundLayerMobile";
                        }
                        if (props[propName]?.mode === e) {
                          props[propName] = undefined;
                        } else {
                          props[propName] = {
                            ...(props[propName] ? props[propName] : {}),
                            mode: e,
                            value:
                              e === "color"
                                ? "#ffffff"
                                : e === "gradient"
                                ? "linear-gradient(135deg, rgb(23,144,214) 0%, rgb(155,81,224) 100%)"
                                : undefined,
                          };
                        }
                      });
                    }}
                    options={[
                      {
                        value: "color",
                        label: "Color",
                        icon: <FontAwesomeIcon icon={faFill} />,
                      },
                      {
                        value: "gradient",
                        label: "Gradient",
                        icon: <FontAwesomeIcon icon={faDroplet} />,
                      },
                      {
                        value: "image",
                        label: "Image",
                        icon: <FontAwesomeIcon icon={faImage} />,
                      },
                    ]}
                  />
                </div>
                <div className="flex relative  text-xs mb-2 items-center">
                  <Responsive
                    activeMode={activeDevice}
                    onChangeActiveMode={(e) => {
                      setActiveDevice(e);
                    }}
                  />
                </div>
              </div>
              {hoverBackground?.mode === "color" && (
                <ColorInput
                  value={hoverBackground?.value}
                  onChange={(val) => {
                    setProp((props) => {
                      var propName;
                      if (activeDevice === "desktop") {
                        propName = "hoverBackgroundLayer";
                      } else if (activeDevice === "tablet") {
                        propName = "hoverBackgroundLayerTablet";
                      } else {
                        propName = "hoverBackgroundLayerMobile";
                      }
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        value: val,
                      };
                    });
                  }}
                  label={"Color"}
                />
              )}
              {hoverBackground?.mode === "gradient" && (
                <>
                  <GradientInput
                    value={hoverBackground?.value}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundLayerTablet";
                        } else {
                          propName = "hoverBackgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          value: val,
                        };
                      });
                    }}
                    label={"gradient"}
                    colorPicker="sketch"
                  />
                  <ToggleInput
                    value={hoverBackground?.fixed}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundLayerTablet";
                        } else {
                          propName = "hoverBackgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          fixed: val,
                        };
                      });
                    }}
                    label={"Fixed"}
                  />
                </>
              )}
              {hoverBackground?.mode === "image" && (
                <>
                  <div className="flex items-center justify-center  cursor-pointer text-sm bg-editor-bg-main-hover duration-300 hover:bg-editor-bg-dark mb-4">
                    <SourceImage
                      projectID={projectID}
                      src={hoverBackground?.image?.src}
                      srcMode={hoverBackground?.image?.srcMode}
                      onClick={() => {
                        setHoverbgImagePickerModal(true);
                      }}
                    />
                  </div>
                  <ImagePickerUIKit
                    src={hoverBackground?.image?.src}
                    srcMode={hoverBackground?.image?.srcMode}
                    onChange={(src, srcMode) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundLayerTablet";
                        } else {
                          propName = "hoverBackgroundLayerMobile";
                        }

                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          image: {
                            ...(props[propName].image
                              ? props[propName].image
                              : {}),
                            src: src,
                            srcMode: srcMode,
                          },
                        };
                      });
                    }}
                    opened={hoverBgImagePickerModal}
                    setOpened={setHoverbgImagePickerModal}
                  />
                  <SelectInput
                    value={hoverBackground?.size}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundLayerTablet";
                        } else {
                          propName = "hoverBackgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          size: val,
                          sizeCustomWidth: "auto",
                          sizeCustomHeight: "auto",
                        };
                      });
                    }}
                    options={[
                      {
                        label: "Cover",
                        value: "cover",
                      },
                      {
                        label: "Contain",
                        value: "contain",
                      },
                      {
                        label: "Custom",
                        value: "custom",
                      },
                    ]}
                    isResetVisible={false}
                    label={"Size"}
                  />
                  {hoverBackground?.size === "custom" && (
                    <>
                      <UnitInput
                        isResetVisible={false}
                        auto
                        value={hoverBackground?.sizeCustomWidth}
                        onChange={(val) => {
                          setProp((props) => {
                            var propName;
                            if (activeDevice === "desktop") {
                              propName = "hoverBackgroundLayer";
                            } else if (activeDevice === "tablet") {
                              propName = "hoverBackgroundLayerTablet";
                            } else {
                              propName = "hoverBackgroundLayerMobile";
                            }
                            props[propName] = {
                              ...(props[propName] ? props[propName] : {}),
                              sizeCustomWidth: val,
                            };
                          });
                        }}
                        label={"Width"}
                      />
                      <UnitInput
                        isResetVisible={false}
                        auto
                        value={hoverBackground?.sizeCustomHeight}
                        onChange={(val) => {
                          setProp((props) => {
                            var propName;
                            if (activeDevice === "desktop") {
                              propName = "hoverBackgroundLayer";
                            } else if (activeDevice === "tablet") {
                              propName = "hoverBackgroundLayerTablet";
                            } else {
                              propName = "hoverBackgroundLayerMobile";
                            }
                            props[propName] = {
                              ...(props[propName] ? props[propName] : {}),
                              sizeCustomHeight: val,
                            };
                          });
                        }}
                        label={"Height"}
                      />
                    </>
                  )}
                  <div className="text-xs  mb-1">Position</div>
                  <div className="flex gap-[5px] justify-between">
                    <UnitInput
                      isResetVisible={false}
                      value={hoverBackground?.positionX}
                      onChange={(val) => {
                        setProp((props) => {
                          var propName;
                          if (activeDevice === "desktop") {
                            propName = "hoverBackgroundLayer";
                          } else if (activeDevice === "tablet") {
                            propName = "hoverBackgroundLayerTablet";
                          } else {
                            propName = "hoverBackgroundLayerMobile";
                          }
                          props[propName] = {
                            ...(props[propName] ? props[propName] : {}),
                            positionX: val,
                          };
                        });
                      }}
                      label={"Left"}
                    />
                    <UnitInput
                      isResetVisible={false}
                      value={hoverBackground?.positionY}
                      onChange={(val) => {
                        setProp((props) => {
                          var propName;
                          if (activeDevice === "desktop") {
                            propName = "hoverBackgroundLayer";
                          } else if (activeDevice === "tablet") {
                            propName = "hoverBackgroundLayerTablet";
                          } else {
                            propName = "hoverBackgroundLayerMobile";
                          }
                          props[propName] = {
                            ...(props[propName] ? props[propName] : {}),
                            positionY: val,
                          };
                        });
                      }}
                      label={"Top"}
                    />
                  </div>
                  <SelectInput
                    value={hoverBackground?.repeat}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundLayerTablet";
                        } else {
                          propName = "hoverBackgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          repeat: val,
                        };
                      });
                    }}
                    options={[
                      {
                        label: "Both",
                        value: "repeat",
                      },
                      {
                        label: "X",
                        value: "repeat-x",
                      },
                      {
                        label: "Y",
                        value: "repeat-y",
                      },
                      {
                        label: "No Repeat",
                        value: "no-repeat",
                      },
                    ]}
                    isResetVisible={false}
                    label={"Repeat"}
                  />
                  <ToggleInput
                    value={hoverBackground?.fixed}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundLayerTablet";
                        } else {
                          propName = "hoverBackgroundLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          fixed: val,
                        };
                      });
                    }}
                    label={"Fixed"}
                  />
                </>
              )}
            </>
          )}
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={
              transition?.types?.[
                ("color", "background-color", "background", "opacity")
              ]?.duration
            }
            onChange={(val) => {
              changeTransition(
                ["color", "background-color", "background", "opacity"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    const backgroundOverlayTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBackgroundOverlaySettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBackgroundOverlaySettings(),
      },
    ];
    function normalBackgroundOverlaySettings() {
      return (
        <>
          <div className="flex">
            <div className="flex flex-1">
              <RadioButtonInput
                className={"w-full"}
                value={backgroundOverlay?.mode}
                onChange={(e) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "backgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "backgroundOverlayLayerTablet";
                    } else {
                      propName = "backgroundOverlayLayerMobile";
                    }
                    if (props[propName]?.mode === e) {
                      props[propName] = undefined;
                    } else {
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        mode: e,
                        value:
                          e === "color"
                            ? "#ffffff"
                            : e === "gradient"
                            ? "linear-gradient(135deg, rgb(23,144,214) 0%, rgb(155,81,224) 100%)"
                            : undefined,
                      };
                    }
                  });
                }}
                options={[
                  {
                    value: "color",
                    label: "Color",
                    icon: <FontAwesomeIcon icon={faFill} />,
                  },
                  {
                    value: "gradient",
                    label: "Gradient",
                    icon: <FontAwesomeIcon icon={faDroplet} />,
                  },
                  {
                    value: "image",
                    label: "Image",
                    icon: <FontAwesomeIcon icon={faImage} />,
                  },
                ]}
              />
            </div>
            <div className="flex relative  text-xs mb-2 items-center">
              <Responsive
                activeMode={activeDevice}
                onChangeActiveMode={(e) => {
                  setActiveDevice(e);
                }}
              />
            </div>
          </div>
          <RangeInput
            resetValue={0.7}
            isResponsive={true}
            value={backgroundOverlayLayerOpacity}
            valueTablet={backgroundOverlayLayerOpacityTablet}
            valueMobile={backgroundOverlayLayerOpacityMobile}
            onChange={(val) => {
              setProp((props) => {
                props.backgroundOverlayLayerOpacity = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.backgroundOverlayLayerOpacityTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.backgroundOverlayLayerOpacityMobile = val;
              });
            }}
            // min={0}
            // max={1}
            // step={0.02}

            min={INPUT_LIMITS["container-opacity"].min}
            max={INPUT_LIMITS["container-opacity"].max}
            step={INPUT_LIMITS["container-opacity"].step}
            label={"Opacity"}
          />
          {backgroundOverlay?.mode === "color" && (
            <ColorInput
              value={backgroundOverlay?.value}
              onChange={(val) => {
                setProp((props) => {
                  var propName;
                  if (activeDevice === "desktop") {
                    propName = "backgroundOverlayLayer";
                  } else if (activeDevice === "tablet") {
                    propName = "backgroundOverlayLayerTablet";
                  } else {
                    propName = "backgroundOverlayLayerMobile";
                  }
                  props[propName] = {
                    ...(props[propName] ? props[propName] : {}),
                    value: val,
                  };
                });
              }}
              label={"Color"}
            />
          )}
          {backgroundOverlay?.mode === "gradient" && (
            <>
              <GradientInput
                value={backgroundOverlay?.value}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "backgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "backgroundOverlayLayerTablet";
                    } else {
                      propName = "backgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      value: val,
                    };
                  });
                }}
                label={"gradient"}
                colorPicker="sketch"
              />
              <ToggleInput
                value={backgroundOverlay?.fixed}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "backgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "backgroundOverlayLayerTablet";
                    } else {
                      propName = "backgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      fixed: val,
                    };
                  });
                }}
                label={"Fixed"}
              />
            </>
          )}
          {backgroundOverlay?.mode === "image" && (
            <>
              <div className="flex items-center justify-center  cursor-pointer text-sm bg-editor-bg-main-hover duration-300 hover:bg-editor-bg-dark mb-4">
                <SourceImage
                  projectID={projectID}
                  src={backgroundOverlay?.image?.src}
                  srcMode={backgroundOverlay?.image?.srcMode}
                  onClick={() => {
                    setbgImagePickerModal(true);
                  }}
                />
              </div>
              <ImagePickerUIKit
                src={backgroundOverlay?.image?.src}
                srcMode={backgroundOverlay?.image?.srcMode}
                onChange={(src, srcMode) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "backgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "backgroundOverlayLayerTablet";
                    } else {
                      propName = "backgroundOverlayLayerMobile";
                    }

                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      image: {
                        ...(props[propName].image ? props[propName].image : {}),
                        src: src,
                        srcMode: srcMode,
                      },
                    };
                  });
                }}
                opened={bgImagePickerModal}
                setOpened={setbgImagePickerModal}
              />
              <SelectInput
                value={backgroundOverlay?.size}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "backgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "backgroundOverlayLayerTablet";
                    } else {
                      propName = "backgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      size: val,
                      sizeCustomWidth: "auto",
                      sizeCustomHeight: "auto",
                    };
                  });
                }}
                options={[
                  {
                    label: "Cover",
                    value: "cover",
                  },
                  {
                    label: "Contain",
                    value: "contain",
                  },
                  {
                    label: "Custom",
                    value: "custom",
                  },
                ]}
                isResetVisible={false}
                label={"Size"}
              />
              {backgroundOverlay?.size === "custom" && (
                <>
                  <UnitInput
                    isResetVisible={false}
                    auto
                    value={backgroundOverlay?.sizeCustomWidth}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundOverlayLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundOverlayLayerTablet";
                        } else {
                          propName = "backgroundOverlayLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          sizeCustomWidth: val,
                        };
                      });
                    }}
                    label={"Width"}
                  />
                  <UnitInput
                    isResetVisible={false}
                    auto
                    value={backgroundOverlay?.sizeCustomHeight}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "backgroundOverlayLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "backgroundOverlayLayerTablet";
                        } else {
                          propName = "backgroundOverlayLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          sizeCustomHeight: val,
                        };
                      });
                    }}
                    label={"Height"}
                  />
                </>
              )}
              <div className="text-xs  mb-1">Position</div>
              <div className="flex gap-[5px] justify-between">
                <UnitInput
                  isResetVisible={false}
                  value={backgroundOverlay?.positionX}
                  onChange={(val) => {
                    setProp((props) => {
                      var propName;
                      if (activeDevice === "desktop") {
                        propName = "backgroundOverlayLayer";
                      } else if (activeDevice === "tablet") {
                        propName = "backgroundOverlayLayerTablet";
                      } else {
                        propName = "backgroundOverlayLayerMobile";
                      }
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        positionX: val,
                      };
                    });
                  }}
                  label={"Left"}
                />
                <UnitInput
                  isResetVisible={false}
                  value={backgroundOverlay?.positionY}
                  onChange={(val) => {
                    setProp((props) => {
                      var propName;
                      if (activeDevice === "desktop") {
                        propName = "backgroundOverlayLayer";
                      } else if (activeDevice === "tablet") {
                        propName = "backgroundOverlayLayerTablet";
                      } else {
                        propName = "backgroundOverlayLayerMobile";
                      }
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        positionY: val,
                      };
                    });
                  }}
                  label={"Top"}
                />
              </div>
              <SelectInput
                value={backgroundOverlay?.repeat}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "backgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "backgroundOverlayLayerTablet";
                    } else {
                      propName = "backgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      repeat: val,
                    };
                  });
                }}
                options={[
                  {
                    label: "Both",
                    value: "repeat",
                  },
                  {
                    label: "X",
                    value: "repeat-x",
                  },
                  {
                    label: "Y",
                    value: "repeat-y",
                  },
                  {
                    label: "No Repeat",
                    value: "no-repeat",
                  },
                ]}
                isResetVisible={false}
                label={"Repeat"}
              />
              <ToggleInput
                value={backgroundOverlay?.fixed}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "backgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "backgroundOverlayLayerTablet";
                    } else {
                      propName = "backgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      fixed: val,
                    };
                  });
                }}
                label={"Fixed"}
              />
            </>
          )}
        </>
      );
    }
    function hoverBackgroundOverlaySettings() {
      return (
        <>
          <div className="flex">
            <div className="flex flex-1">
              <RadioButtonInput
                className={"w-full"}
                value={hoverBackgroundOverlay?.mode}
                onChange={(e) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "hoverBackgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "hoverBackgroundOverlayLayerTablet";
                    } else {
                      propName = "hoverBackgroundOverlayLayerMobile";
                    }

                    if (e === undefined) {
                      props[propName] = undefined;
                      return;
                    }

                    if (props[propName]?.mode === e) {
                      props[propName] = undefined;
                    } else {
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        mode: e,
                        value:
                          e === "color"
                            ? "#ffffff"
                            : e === "gradient"
                            ? "linear-gradient(135deg, rgb(23,144,214) 0%, rgb(155,81,224) 100%)"
                            : undefined,
                      };
                    }
                  });
                }}
                options={[
                  {
                    value: "color",
                    label: "Color",
                    icon: <FontAwesomeIcon icon={faFill} />,
                  },
                  {
                    value: "gradient",
                    label: "Gradient",
                    icon: <FontAwesomeIcon icon={faDroplet} />,
                  },
                  {
                    value: "image",
                    label: "Image",
                    icon: <FontAwesomeIcon icon={faImage} />,
                  },
                ]}
              />
            </div>
            <div className="flex relative  text-xs mb-2 items-center">
              <Responsive
                activeMode={activeDevice}
                onChangeActiveMode={(e) => {
                  setActiveDevice(e);
                }}
              />
            </div>
          </div>
          <RangeInput
            resetValue={0.7}
            isResponsive={true}
            value={hoverBackgroundOverlayLayerOpacity}
            valueTablet={hoverBackgroundOverlayLayerOpacityTablet}
            valueMobile={hoverBackgroundOverlayLayerOpacityMobile}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBackgroundOverlayLayerOpacity = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverBackgroundOverlayLayerOpacityTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverBackgroundOverlayLayerOpacityMobile = val;
              });
            }}
            // min={0}
            // max={1}
            // step={0.02}

            min={INPUT_LIMITS["container-opacity"].min}
            max={INPUT_LIMITS["container-opacity"].max}
            step={INPUT_LIMITS["container-opacity"].step}
            label={"Opacity"}
          />
          {hoverBackgroundOverlay?.mode === "color" && (
            <ColorInput
              value={hoverBackgroundOverlay?.value}
              onChange={(val) => {
                setProp((props) => {
                  var propName;
                  if (activeDevice === "desktop") {
                    propName = "hoverBackgroundOverlayLayer";
                  } else if (activeDevice === "tablet") {
                    propName = "hoverBackgroundOverlayLayerTablet";
                  } else {
                    propName = "hoverBackgroundOverlayLayerMobile";
                  }
                  props[propName] = {
                    ...(props[propName] ? props[propName] : {}),
                    value: val,
                  };
                });
              }}
              label={"Color"}
            />
          )}
          {hoverBackgroundOverlay?.mode === "gradient" && (
            <>
              <GradientInput
                value={hoverBackgroundOverlay?.value}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "hoverBackgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "hoverBackgroundOverlayLayerTablet";
                    } else {
                      propName = "hoverBackgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      value: val,
                    };
                  });
                }}
                label={"gradient"}
                colorPicker="sketch"
              />
              <ToggleInput
                value={hoverBackgroundOverlay?.fixed}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "hoverBackgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "hoverBackgroundOverlayLayerTablet";
                    } else {
                      propName = "hoverBackgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      fixed: val,
                    };
                  });
                }}
                label={"Fixed"}
              />
            </>
          )}
          {hoverBackgroundOverlay?.mode === "image" && (
            <>
              <div className="flex items-center justify-center  cursor-pointer text-sm bg-editor-bg-main-hover duration-300 hover:bg-editor-bg-dark mb-4">
                <SourceImage
                  projectID={projectID}
                  src={hoverBackgroundOverlay?.image?.src}
                  srcMode={hoverBackgroundOverlay?.image?.srcMode}
                  onClick={() => {
                    setbgImagePickerModal(true);
                  }}
                />
              </div>
              <ImagePickerUIKit
                src={hoverBackgroundOverlay?.image?.src}
                srcMode={hoverBackgroundOverlay?.image?.srcMode}
                onChange={(src, srcMode) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "hoverBackgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "hoverBackgroundOverlayLayerTablet";
                    } else {
                      propName = "hoverBackgroundOverlayLayerMobile";
                    }

                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      image: {
                        ...(props[propName].image ? props[propName].image : {}),
                        src: src,
                        srcMode: srcMode,
                      },
                    };
                  });
                }}
                opened={bgImagePickerModal}
                setOpened={setbgImagePickerModal}
              />
              <SelectInput
                value={hoverBackgroundOverlay?.size}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "hoverBackgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "hoverBackgroundOverlayLayerTablet";
                    } else {
                      propName = "hoverBackgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      size: val,
                      sizeCustomWidth: "auto",
                      sizeCustomHeight: "auto",
                    };
                  });
                }}
                options={[
                  {
                    label: "Cover",
                    value: "cover",
                  },
                  {
                    label: "Contain",
                    value: "contain",
                  },
                  {
                    label: "Custom",
                    value: "custom",
                  },
                ]}
                isResetVisible={false}
                label={"Size"}
              />
              {hoverBackgroundOverlay?.size === "custom" && (
                <>
                  <UnitInput
                    isResetVisible={false}
                    auto
                    value={hoverBackgroundOverlay?.sizeCustomWidth}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundOverlayLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundOverlayLayerTablet";
                        } else {
                          propName = "hoverBackgroundOverlayLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          sizeCustomWidth: val,
                        };
                      });
                    }}
                    label={"Width"}
                  />
                  <UnitInput
                    isResetVisible={false}
                    auto
                    value={hoverBackgroundOverlay?.sizeCustomHeight}
                    onChange={(val) => {
                      setProp((props) => {
                        var propName;
                        if (activeDevice === "desktop") {
                          propName = "hoverBackgroundOverlayLayer";
                        } else if (activeDevice === "tablet") {
                          propName = "hoverBackgroundOverlayLayerTablet";
                        } else {
                          propName = "hoverBackgroundOverlayLayerMobile";
                        }
                        props[propName] = {
                          ...(props[propName] ? props[propName] : {}),
                          sizeCustomHeight: val,
                        };
                      });
                    }}
                    label={"Height"}
                  />
                </>
              )}
              <div className="text-xs  mb-1">Position</div>
              <div className="flex gap-[5px] justify-between">
                <UnitInput
                  isResetVisible={false}
                  value={hoverBackgroundOverlay?.positionX}
                  onChange={(val) => {
                    setProp((props) => {
                      var propName;
                      if (activeDevice === "desktop") {
                        propName = "hoverBackgroundOverlayLayer";
                      } else if (activeDevice === "tablet") {
                        propName = "hoverBackgroundOverlayLayerTablet";
                      } else {
                        propName = "hoverBackgroundOverlayLayerMobile";
                      }
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        positionX: val,
                      };
                    });
                  }}
                  label={"Left"}
                />
                <UnitInput
                  isResetVisible={false}
                  value={hoverBackgroundOverlay?.positionY}
                  onChange={(val) => {
                    setProp((props) => {
                      var propName;
                      if (activeDevice === "desktop") {
                        propName = "hoverBackgroundOverlayLayer";
                      } else if (activeDevice === "tablet") {
                        propName = "hoverBackgroundOverlayLayerTablet";
                      } else {
                        propName = "hoverBackgroundOverlayLayerMobile";
                      }
                      props[propName] = {
                        ...(props[propName] ? props[propName] : {}),
                        positionY: val,
                      };
                    });
                  }}
                  label={"Top"}
                />
              </div>
              <SelectInput
                value={hoverBackgroundOverlay?.repeat}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "hoverBackgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "hoverBackgroundOverlayLayerTablet";
                    } else {
                      propName = "hoverBackgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      repeat: val,
                    };
                  });
                }}
                options={[
                  {
                    label: "Both",
                    value: "repeat",
                  },
                  {
                    label: "X",
                    value: "repeat-x",
                  },
                  {
                    label: "Y",
                    value: "repeat-y",
                  },
                  {
                    label: "No Repeat",
                    value: "no-repeat",
                  },
                ]}
                isResetVisible={false}
                label={"Repeat"}
              />
              <ToggleInput
                value={hoverBackgroundOverlay?.fixed}
                onChange={(val) => {
                  setProp((props) => {
                    var propName;
                    if (activeDevice === "desktop") {
                      propName = "hoverBackgroundOverlayLayer";
                    } else if (activeDevice === "tablet") {
                      propName = "hoverBackgroundOverlayLayerTablet";
                    } else {
                      propName = "hoverBackgroundOverlayLayerMobile";
                    }
                    props[propName] = {
                      ...(props[propName] ? props[propName] : {}),
                      fixed: val,
                    };
                  });
                }}
                label={"Fixed"}
              />
            </>
          )}
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["background"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["background"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: textColor },
              { key: linkColor },
              { key: hoverTextColor },
              { key: hoverLinkColor },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <TabsV2 items={colorTabs} />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: isBackgroundLayerDynamic },
              { key: dynamicBackgroundLayerSrc },
              { key: background },
              { key: hoverIsBackgroundLayerDynamic },
              { key: hoverDynamicBackgroundLayerSrc },
              { key: hoverBackground },
            ])}
            id={`block-setting-${id}-background`}
            title={"Background"}
            initialOpen={false}
          >
            <TabsV2 items={backgroundTabs} />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: backgroundOverlay },
              { key: hoverBackgroundOverlay },
            ])}
            id={`block-setting-${id}-background-overlay`}
            title={"Background Overlay"}
            initialOpen={false}
          >
            <TabsV2 items={backgroundOverlayTabs} />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    const marginPaddingTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalMarginPaddingSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverMarginPaddingSettings(),
      },
    ];
    function normalMarginPaddingSettings() {
      return (
        <>
          <MarginInput
            isResponsive={true}
            valueTablet={marginTablet}
            valueMobile={marginMobile}
            resetValue={undefined}
            value={margin}
            onChange={(val) => {
              setProp((props) => {
                props.margin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.marginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.marginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            valueTablet={paddingTablet}
            valueMobile={paddingMobile}
            resetValue={{
              top: "20px",
              bottom: "20px",
              left: "20px",
              right: "20px",
            }}
            value={padding}
            onChange={(val) => {
              setProp((props) => {
                props.padding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.paddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.paddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </>
      );
    }
    function hoverMarginPaddingSettings() {
      return (
        <>
          <MarginInput
            isResponsive={true}
            valueTablet={hoverMarginTablet}
            valueMobile={hoverMarginMobile}
            resetValue={undefined}
            value={hoverMargin}
            onChange={(val) => {
              setProp((props) => {
                props.hoverMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverMarginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            valueTablet={hoverPaddingTablet}
            valueMobile={hoverPaddingMobile}
            resetValue={undefined}
            value={hoverPadding}
            onChange={(val) => {
              setProp((props) => {
                props.hoverPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("margin", "padding")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["margin", "padding"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    const layoutTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalLayoutSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverLayoutSettings(),
      },
    ];
    function normalLayoutSettings() {
      return (
        <>
          <BorderInput
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
            valueTablet={borderTablet}
            setValueTablet={(val) => {
              setProp((props) => {
                props.borderTablet = val;
              });
            }}
            valueMobile={borderMobile}
            setValueMobile={(val) => {
              setProp((props) => {
                props.borderMobile = val;
              });
            }}
          />
          <BorderRadiusInput
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </>
      );
    }
    function hoverLayoutSettings() {
      return (
        <>
          <BorderInput
            value={hoverBorder}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorder = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
            valueTablet={hoverBorderTablet}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverBorderTablet = val;
              });
            }}
            valueMobile={hoverBorderMobile}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverBorderMobile = val;
              });
            }}
          />
          <BorderRadiusInput
            value={hoverBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["border"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["border", "border-radius"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    const boxShadowTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBoxShadowSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBoxShadowSettings(),
      },
    ];
    function normalBoxShadowSettings() {
      return (
        <>
          {" "}
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </>
      );
    }
    function hoverBoxShadowSettings() {
      return (
        <>
          {" "}
          <BoxShadowInput
            resetValue={undefined}
            value={hoverBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBoxShadow = val;
              });
            }}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["box-shadow"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["box-shadow"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: marginTablet },
            { key: marginMobile },
            { key: margin },
            { key: paddingTablet },
            { key: paddingMobile },
            { key: padding },
          ])}
          id={`block-setting-${id}-margin`}
          title={"Layout"}
          initialOpen={false}
        >
          <TabsV2 items={marginPaddingTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderTablet },
            { key: borderMobile },
            { key: borderRadius },
            { key: hoverBorder },
            { key: hoverBorderTablet },
            { key: hoverBorderMobile },
            { key: hoverBorderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <TabsV2 items={layoutTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: boxShadow },
            { key: hoverBoxShadow },
          ])}
          id={`block-setting-${id}-box-shadow`}
          title={"Box Shadow"}
          initialOpen={false}
        >
          <TabsV2 items={boxShadowTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: isComponent },
            { key: newComponentName },
          ])}
          id={`block-setting-${id}-component`}
          title={"Component"}
          initialOpen={false}
        >
          {isComponent ? (
            <>
              <div className=" text-xs mb-2">
                <div>
                  Name: {projectComponents["component-" + componentID]?.title}
                </div>
              </div>
              <div
                onClick={(e) => {
                  setProp((props) => {
                    props.isComponent = undefined;
                    props.componentID = undefined;
                  });

                  // var node_parent = undefined;
                  // var node_index = undefined;
                  // if (parents && parents.length > 0) {
                  //   node_parent = parents[0];
                  // }
                  // if (node_parent) {
                  //   let parentChildrens = node(node_parent).childNodes();
                  //   node_index = parentChildrens.indexOf(id);
                  // }

                  // const componentContent = handleSaveComponent(id, query);

                  // // Add new cloned node tree at current index
                  // if (node_parent !== undefined && node_index !== undefined) {
                  //   addNodeTree(
                  //     componentContent,
                  //     actions,
                  //     query,
                  //     node_parent,
                  //     node_index,
                  //   );
                  //   // actions.delete(id);
                  // }
                }}
                className=" text-xs bg-editor-input-selected px-2 py-2 rounded-sm text-center cursor-pointer hover:bg-editor-bg-main"
              >
                Detach Component
              </div>
            </>
          ) : (
            <>
              {isCreatingComponent && (
                <TextInput
                  value={newComponentName}
                  onChange={(val) => {
                    setNewComponentName(val);
                  }}
                  placeholder="Component"
                  label="Name"
                  minLength={INPUT_LIMITS["component-name"].minlength}
                  maxLength={INPUT_LIMITS["component-name"].maxlength}
                />
              )}
              <div
                onClick={(e) => {
                  if (isCreatingComponent) {
                    var componentID = generate_new_id();
                    setProp((props) => {
                      props.isComponent = true;
                      props.componentID = componentID;
                    });
                    const componentContent = handleSaveComponent(id, query);
                    addComponent(
                      newComponentName
                        ? newComponentName
                        : "Component - " +
                            Object.keys(projectComponents ?? {}).length,
                      componentContent,
                      id,
                      componentID,
                    );
                    setIsCreatingComponent(false);
                    setNewComponentName("");
                  } else {
                    setIsCreatingComponent(true);
                  }
                }}
                className="text-xs bg-editor-input-selected border px-2 py-2 rounded-sm text-center cursor-pointer hover:bg-editor-bg-main"
              >
                Create Component
              </div>
            </>
          )}
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: display, default: "block" },
        { key: direction, default: "column" },
        { key: directionTablet },
        { key: directionMobile },
        { key: wrap, default: "wrap" },
        { key: wrapTablet },
        { key: wrapMobile },
        { key: alignItemsTablet },
        { key: alignItemsMobile },
        { key: alignItems, default: "start" },
        { key: justifyContentTablet },
        { key: justifyContentMobile },
        { key: justifyContent, default: "start" },
        { key: blockGap, default: "1rem" },
        { key: blockGapTablet },
        { key: blockGapMobile },
        { key: width, default: "100%" },
        { key: widthTablet, default: "100%" },
        { key: widthMobile, default: "100%" },
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
        { key: maxHeight },
        { key: maxHeightTablet },
        { key: maxHeightMobile },
        { key: maxWidthTablet },
        { key: maxWidth },
        { key: maxWidthMobile },
        { key: minHeightMobile },
        { key: minHeightTablet },
        { key: minHeight },
        { key: minWidth },
        { key: minWidthMobile },
        { key: minWidthTablet },
        { key: Tag, default: "div" },
        { key: overflow },
        { key: marginTablet },
        { key: marginMobile },
        { key: margin },
        { key: paddingTablet },
        { key: paddingMobile },
        {
          key: padding,
          default: {
            top: "20px",
            bottom: "20px",
            left: "20px",
            right: "20px",
          },
        },

        { key: url },
        { key: openInNewTab },
        { key: linkDecoration },
        { key: isUrlDynamic },
        { key: dynamicUrlSrc },
        { key: hoverMarginTablet },
        { key: hoverMarginMobile },
        { key: hoverMargin },
        { key: hoverPaddingTablet },
        { key: hoverPaddingMobile },
        { key: hoverPadding },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: linkColor },
        { key: isBackgroundLayerDynamic },
        { key: dynamicBackgroundLayerSrc },
        { key: background },
        { key: hoverBackground },
        { key: backgroundOverlay },
        { key: hoverBackgroundOverlay },
        { key: hoverTextColor },
        { key: hoverLinkColor },
        { key: hoverIsBackgroundLayerDynamic },
        { key: hoverDynamicBackgroundLayerSrc },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: border },
        { key: borderTablet },
        { key: borderMobile },
        { key: boxShadow },
        { key: borderRadius },
        { key: isComponent },
        { key: newComponentName },
        { key: hoverBorder },
        { key: hoverBorderTablet },
        { key: hoverBorderMobile },
        { key: hoverBorderRadius },
        { key: hoverBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
